import React, { useCallback, useMemo } from "react";
import { Avatar, styled } from "@mui/material";
import { StaffIcon } from "components/icon";
import { useTranslation } from "react-i18next";

/**
 * Predefined pastel-ish color palette.
 * Each user will get a consistent background color based on their name.
 */
const colors = [
  "#FFB6C1", // light pink
  "#FFD700", // gold
  "#87CEFA", // light sky blue
  "#32CD32", // lime green
  "#FFA500", // orange
  "#9370DB", // medium purple
  "#00CED1", // dark turquoise
  "#FF69B4", // hot pink
];

/**
 * Generate a deterministic color from a string (usually the user's name).
 * Uses a simple hash + modulo to pick from our palette.
 */
const stringToPaletteColor = (str) => {
  if (!str) return "#F2F4F7"; // fallback neutral color
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  // Pick a color index based on hash
  return colors[Math.abs(hash) % colors.length];
};

/**
 * Calculate contrast text color (black or white) depending on background brightness.
 * Ensures initials/icons are always readable.
 */
const getContrastText = (hexColor) => {
  if (!hexColor) return "#000000";
  const c = hexColor.substring(1); // remove "#"
  const rgb = parseInt(c, 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = rgb & 0xff;

  // Formula for perceived brightness
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  // If bright background → return black text, else white
  return brightness > 150 ? "#475467" : "#FFFFFF";
};

// Styled MUI Avatar base component
const StyledAvatar = styled(Avatar)(() => ({
  width: 36,
  height: 36
}));

/**
 * DynamicAvatar Component
 *
 * Behavior:
 * 1. If `src` (image URL) works → show the image.
 * 2. If image fails and `alt` is provided → show initials from the name.
 * 3. If no `alt` → show a default <StaffIcon />.
 * 4. If `children` is passed → override all fallbacks and render that directly.
 * 5. Background color is generated from the `alt` (name) for consistency.
 * 6. Text color auto-adjusts (black/white) based on background brightness.
 */
const DynamicAvatar = ({
  src,                       // profile image URL
  alt,                       // full name (used to derive initials)
  fallbackSrc,               // optional fallback image if `src` fails
  isOnlyFirstLetter = false, // if true → show only first letter instead of first+last
  children,                  // manual override for avatar content (emoji/icon/etc.)
  sx,                        // custom styling
  isDynamicBackgroundColor = false, // toggle name-based background coloring
  ...props                   // forward all other props
}) => {
  const { t } = useTranslation();
  /**
   * Image load error handler.
   * If the main image fails, and a `fallbackSrc` exists → replace it.
   */
  const handleError = useCallback(
    (e) => {
      if (fallbackSrc) {
        e.currentTarget.onerror = null; // prevent infinite error loop
        e.currentTarget.src = fallbackSrc;
      }
    },
    [fallbackSrc]
  );

  /**
   * Decide what content goes inside the Avatar:
   * - `children` (highest priority)
   * - Initials from name (if available)
   * - Default <StaffIcon /> (last fallback)
   */
  const renderChildren = useMemo(() => {
    const getInitials = (fullName) => {
      if (!fullName) return "";
      const parts = fullName.trim().split(/\s+/);
      const first = parts[0]?.[0] || ""; // first letter of first word
      const last =
        parts.length > 1 && !isOnlyFirstLetter
          ? parts[parts.length - 1][0] // first letter of last word
          : "";
      return (first + (isOnlyFirstLetter ? "" : last)).toUpperCase();
    };

    if (children) return children; // custom content overrides everything
    if (alt) return getInitials(alt); // show initials if name is provided
    return (
      <StaffIcon fontSize="small" sx={{ color: "#475467" }} color="#475467" />
    ); // default icon if no name
  }, [children, alt, isOnlyFirstLetter]);

  /** Background + text color */
  const backgroundColor =
    alt && isDynamicBackgroundColor ? stringToPaletteColor(alt) : "#F2F4F7";
  const textColor = getContrastText(backgroundColor);

  return (
    <StyledAvatar
      src={src}
      alt={alt}
      // a named image (a plain div may not carry aria-label)
      role="img"
      aria-label={alt || t("userAvatar")}
      onError={handleError}
      sx={{
        fontSize:'inherit',
        backgroundColor,
        color: textColor,
        ...sx, // merge custom styles
      }}
      {...props}
    >
      {renderChildren}
    </StyledAvatar>
  );
};

export default React.memo(DynamicAvatar);
